import { tenantUrl, toUrlRid } from '../../utils/tenantPath';
import { useEffect } from 'react';
import Icon from '../../components/Icon';
import { ROLE_COLORS, ROLE_LABELS } from './modals';
import { formatUtcDay } from '../../utils/dateFormat';

// Small shared pieces for the Command Center screens. Kept together so the
// loading / error / empty triple behaves identically everywhere — that
// consistency is the point, not the code saving.

/**
 * Refetch whenever this tab regains focus.
 *
 * The console and the customer app are two tabs over ONE database with no cache
 * in between, so a change made in either is instantly true for the other — but
 * only a screen that asks again will show it. Without this, you fix a Dokka
 * address in the app, switch back to the console, and it still shows the old
 * one; the data was never stale, the screen was.
 *
 * Focus rather than polling: better-sqlite3 is synchronous and single-process,
 * and this is the pattern the customer navbar's drafts badge already uses.
 *
 * Both listeners are needed — alt-tabbing between two visible windows fires
 * only 'focus', switching browser tabs fires both — but returning to a tab
 * fires BOTH, which used to run every load twice back to back. The cooldown
 * collapses that pair (and any focus flurry) into one fetch; a second of
 * staleness is invisible next to a refetch that only fires on user action.
 */
export function useRefetchOnFocus(load) {
  useEffect(() => {
    let last = 0;
    const onFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - last < 1000) return;
      last = now;
      load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);
}

/**
 * THE cross-tenant handoff. Opens a customer-app path in a new tab with the
 * restaurant riding in the URL; TenantGate validates access inside that tab. The console itself has no tenant and never writes a
 * restaurant hint; existing customer tabs keep their own URLs.
 *
 * One exported copy, used by every console surface that opens a document
 * (queues, customer card, health). Explicit prefixed URLs survive new tabs
 * and refresh without reading a shared restaurant hint. Invalid ids never
 * open a tab.
 */
export function openInApp(path, restaurantId) {
  if (toUrlRid(restaurantId) == null) return false;
  window.open(tenantUrl(restaurantId, path), '_blank', 'noopener');
  return true;
}

/**
 * openInApp for a tab whose address is known only AFTER a request — the
 * מחוקים restore. A window.open after an `await` is outside the click, and
 * Safari blocks it silently; so the tab is opened blank inside the click and
 * pointed at the document once the answer arrives (or closed on failure).
 * Its opener is cut, as 'noopener' would. Falls back to openInApp when the
 * browser refused even the blank tab.
 */
export function openPendingTab() {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  return {
    go(path, restaurantId) {
      if (toUrlRid(restaurantId) == null) { tab?.close(); return false; }
      if (!tab) return openInApp(path, restaurantId);
      tab.location.href = tenantUrl(restaurantId, path);
      return true;
    },
    cancel() { tab?.close(); },
  };
}

/**
 * Loading, failure and empty are THREE states, never two.
 *
 * The old admin page collapsed failure into empty (a caught networks fetch
 * rendered the friendly "אין רשתות — צרו רשת" copy), and an ops person reading
 * that goes and creates a duplicate. A zero and a failure must never render the
 * same pixel, so failure gets its own treatment and a retry.
 */
export function LoadState({ loading, error, onRetry, empty, emptyMessage, children }) {
  if (error) {
    return (
      <div className="cc-error">
        <Icon name="alert" size={17} />
        <span>לא הצלחנו לטעון את הנתונים</span>
        <span className="cc-spacer" />
        {onRetry && (
          <button className="btn btn-sm btn-secondary" onClick={onRetry}>
            <Icon name="undo" size={13} /> נסה שוב
          </button>
        )}
      </div>
    );
  }
  if (loading) return <div className="cc-empty">טוען…</div>;
  if (empty) return <div className="cc-empty">{emptyMessage}</div>;
  return children;
}

export function RoleBadge({ role }) {
  const color = ROLE_COLORS[role] || '#6b7280';
  return (
    <span className="cc-badge" style={{ background: `${color}18`, color }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

/**
 * The invite / password-link state.
 *
 * Says "קישור סיסמה", never "מעולם לא התחבר": the invite flow and the
 * forgot-password flow share one token table, so the data genuinely cannot tell
 * "invited and never got in" from "asked for a reset yesterday". users.last_login_at
 * (Cut 2) makes it exact. Until then the label claims only what's true.
 */
export function InvitePill({ invite }) {
  if (!invite || invite.state === 'active') return <span className="cc-pill ok">פעיל</span>;
  if (invite.state === 'expired') return <span className="cc-pill bad">הקישור פג</span>;
  return <span className="cc-pill warn">קישור סיסמה ממתין</span>;
}

// Moved to utils/dateFormat.js so customer screens can share it without
// pulling in the console's modules.
export const formatDate = formatUtcDay;

/**
 * Age of the oldest item in a queue, in days.
 *
 * Age is the only column that turns a backlog into a priority — two drafts from
 * nine days ago beat thirty from this morning. The thresholds REUSE the overdue
 * vocabulary `dueDateStatus` already teaches every user of this product
 * (>7 alert, 3–7 warn, <3 neutral) rather than inventing a second scale.
 */
export function AgeChip({ days }) {
  if (days == null) return null;
  const tone = days > 7 ? 'hot' : days >= 3 ? 'warm' : 'cool';
  const label = days === 0 ? 'היום' : days === 1 ? 'יום' : `${days} ימים`;
  return <span className={`cc-age is-${tone}`}>{label}</span>;
}

/**
 * Last login, stamped by POST /api/auth/login.
 *
 * A missing value means "we have no record", NOT "never logged in" — the column
 * was added in Cut 2 and deliberately not backfilled, so every account that
 * predates it reads empty until its owner next signs in. Saying "מעולם לא נכנס"
 * here would be a confident lie about the oldest and most established users in
 * the system, so the cell shows a dash and says what it means on hover.
 */
export function LastLogin({ value }) {
  if (!value) {
    return <span className="cc-muted" title="לא תועדה התחברות — לא בהכרח אומר שלא נכנס">—</span>;
  }
  return <span className="cc-num">{formatDate(value)}</span>;
}

/** A yes/no capability line on the user card. */
export function Capability({ label, allowed }) {
  return (
    <span className={`cc-cap${allowed ? '' : ' no'}`}>
      <Icon name={allowed ? 'check' : 'close'} size={14} className={allowed ? 'yes-ic' : undefined} />
      {label}
    </span>
  );
}
